import { apiService } from './api.service'

export interface ActiveDirectoryConfig {
  id: number
  organization: number
  is_enabled: boolean
  server_uri: string
  use_ssl: boolean
  use_start_tls: boolean
  validate_cert: boolean
  ca_cert_pem: string
  bind_dn: string
  bind_password_set: boolean
  user_search_base: string
  user_search_filter: string
  attr_username: string
  attr_email: string
  attr_first_name: string
  attr_last_name: string
  auto_create_users: boolean
  sync_attributes_on_login: boolean
  default_group_ids: number[]
  default_organization_ids: number[]
  timeout_seconds: number
  last_test_at: string | null
  last_test_ok: boolean | null
  last_test_message: string
}

export type ActiveDirectoryConfigPatch = Partial<
  Omit<ActiveDirectoryConfig, 'bind_password_set' | 'last_test_at' | 'last_test_ok' | 'last_test_message'>
> & {
  bind_password?: string
}

export interface ADTestConnectionResult {
  success: boolean
  message: string
}

export interface ADUserRecord {
  dn: string
  username: string
  email: string
  first_name: string
  last_name: string
  exists_in_dms: boolean
}

export interface ADSearchUsersResult {
  success: boolean
  count: number
  users: ADUserRecord[]
  message?: string
}

export interface ADImportResultItem {
  username: string
  status: 'created' | 'updated' | 'not_found' | 'error'
  message?: string
  user_id?: number
}

export interface ADImportUsersResult {
  success: boolean
  results: ADImportResultItem[]
}

class ActiveDirectoryService {
  async getConfig(): Promise<ActiveDirectoryConfig | null> {
    const response = await apiService.get<{ results: ActiveDirectoryConfig[] }>('active_directory/')
    const results = (response.data as any)?.results ?? response.data
    if (Array.isArray(results) && results.length > 0) {
      return results[0]
    }
    return null
  }

  async updateConfig(id: number, patch: ActiveDirectoryConfigPatch): Promise<ActiveDirectoryConfig> {
    const response = await apiService.patch<ActiveDirectoryConfig>(`active_directory/${id}/`, patch)
    return response.data
  }

  async testConnection(overrides?: ActiveDirectoryConfigPatch): Promise<ADTestConnectionResult> {
    try {
      const response = await apiService.post<ADTestConnectionResult>('active_directory/test_connection/', overrides ?? {})
      return response.data
    } catch (error: any) {
      const data = error?.response?.data
      return {
        success: false,
        message: data?.message || 'Connection failed.',
      }
    }
  }

  async searchUsers(
    query: string,
    overrides?: ActiveDirectoryConfigPatch & { limit?: number }
  ): Promise<ADSearchUsersResult> {
    try {
      const response = await apiService.post<ADSearchUsersResult>('active_directory/search_users/', {
        query,
        limit: overrides?.limit ?? 200,
        ...(overrides ?? {}),
      })
      return response.data
    } catch (error: any) {
      const data = error?.response?.data
      return {
        success: false,
        count: 0,
        users: [],
        message: data?.message || 'Search failed.',
      }
    }
  }

  async importUsers(
    usernames: string[],
    options?: { is_active?: boolean; group_ids?: number[] }
  ): Promise<ADImportUsersResult> {
    const response = await apiService.post<ADImportUsersResult>('active_directory/import_users/', {
      usernames,
      is_active: options?.is_active ?? true,
      group_ids: options?.group_ids ?? [],
    })
    return response.data
  }

  async syncUser(userId: number): Promise<{ success: boolean; changed_fields: string[]; message?: string }> {
    try {
      const response = await apiService.post<{ success: boolean; changed_fields: string[] }>(
        'active_directory/sync_user/',
        { user_id: userId }
      )
      return response.data
    } catch (error: any) {
      const data = error?.response?.data
      return {
        success: false,
        changed_fields: [],
        message: data?.message || 'Sync failed.',
      }
    }
  }
}

export const activeDirectoryService = new ActiveDirectoryService()
